import { Box, Button, Input, Text } from '@wix/design-system'
import { useContent, useNext } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useDebouncedTypingLogger, usePageLogger } from '../../../../bi/mfi'
import { WixMobileBackButton } from '../../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileContentWrapper } from '../../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileFooter } from '../../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobilePage } from '../../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { STORES_FUNNEL_QUERY_PARAMS } from '../StoresFunnelUpgrade/StoresFunnelUpgrade'
import { Content } from './StoresFunnelBusinessName.data'
import { storesFunnelBusinessNameDataHooks as DataHooks } from './StoresFunnelBusinessName.dataHooks'
import s from './StoresFunnelBusinessName.scss'

export const StoresFunnelBusinessName: FC = () => {
  const [businessName, setBusinessName] = useState('')
  const { content } = useContent<Content>()
  const next = useNext()
  const pageLogger = usePageLogger()
  const debouncedTyping = useDebouncedTypingLogger(pageLogger)
  const [searchParams] = useSearchParams()

  const categoryKey = searchParams.get(STORES_FUNNEL_QUERY_PARAMS.categoryKey) || 'something_else'
  const placeholder = categoryKeyToPlaceholder[categoryKey]
  const { title, subtitle } = content.question.content

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const onSkipButtonClick = () => {
    pageLogger.skip({
      payload: { businessName },
    })

    next({ [content.question.questionKey]: { skipped: true } })
  }

  const onNextButtonClick = () => {
    pageLogger.answer({
      answer: { businessName },
      answerType: 'free text',
      freeText: businessName,
    })

    next({ [content.question.questionKey]: { values: [businessName] } })
  }

  const onBackButtonClick = () => {
    pageLogger.back({
      payload: { businessName },
    })

    window.history.back()
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBusinessName(e.target.value)
    debouncedTyping(e.target.value)
  }

  const isNextDisabled = businessName.length < 3

  return (
    <WixMobilePage>
      <WixMobileHeader
        leftContent={
          (content.back && (
            <WixMobileBackButton dataHook={DataHooks.back()} onBack={onBackButtonClick} label={content.back} />
          )) ||
          undefined
        }
        rightContent={
          (content.skip && (
            <WixMobileSkipButton dataHook={DataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
          )) ||
          undefined
        }
      />
      <WixMobileContentWrapper>
        {content.stepProgressBar.content.visible && (
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            dataHook={DataHooks.stepProgressBar()}
          />
        )}
        <WixMobileHeading dataHook={DataHooks.title()}>{title}</WixMobileHeading>
        <Text dataHook={DataHooks.subtitle()} className={s.businessName_subtitle}>
          {subtitle}
        </Text>

        <Box width="100%">
          <Input
            className={s.businessNameInput}
            size="large"
            value={businessName}
            onChange={onInputChange}
            placeholder={placeholder}
            data-hook={DataHooks.input()}
          />
        </Box>
      </WixMobileContentWrapper>

      {content.next && (
        <WixMobileFooter>
          <Button
            size="large"
            fullWidth
            disabled={isNextDisabled}
            onClick={onNextButtonClick}
            dataHook={DataHooks.next()}
          >
            {content.next}
          </Button>
        </WixMobileFooter>
      )}
    </WixMobilePage>
  )
}

const categoryKeyToPlaceholder: Record<string, string> = {
  fashion_apparel: 'e.g., Clothing & Fashion Store',
  beauty_personal_care: 'e.g., Beauty & Personal Care Shop',
  home_living: 'e.g., Home Goods Store',
  electronics_gadgets: 'e.g., Electronics & Gadgets Shop',
  sports_outdoors: 'e.g., Sporting Goods Store',
  food_beverages: 'e.g., Food & Beverage Outlet',
  toys_kids_baby: 'e.g., Kids, Toys & Baby Store',
  books_media_art: 'e.g., Books, Media & Art Store',
  something_else: 'e.g., My Awesome Store',
}
